import React from 'react';
import {
    createDirectBookingUrl,
    updateDirectBookingButtons,
    getDirectBookingThreshold,
} from '../../helpers/directBookingHelper';
import { getOnBoardCredit } from '../../helpers/priceHelpers';
import { getRouteSlugs } from '../../helpers/routing';
import { __ } from '../../helpers/TranslationService';
import { useActions } from '../../hooks/useActions';
import usePersonalDataValidation from '../../hooks/usePersonalData';
import { isChildAgeValid } from './helpers';

import { ICabinInfo, ICruiseReducer } from '../../interfaces';
import { IRequestFormReducer } from '../../interfaces/IRequestForm';
import { IPageDataReducer } from '../../reducers/pageData';
import { RequestFormButtons } from './RequestFormButtons';
import { RequestFormButtonsV2 } from './RequestFormButtonsV2';
import RequestFormCabins from './RequestFormCabins';
import RequestFormCabinType from './RequestFormCabinType';
import RequestFormDates from './RequestFormDates';
import RequestFormPersonalInfo from './RequestFormPersonalInfo';
import RequestFormSummary from './RequestFormSummary';
import RequestFormTravelers from './RequestFormTravelers';

import { BookingRequestType } from '../../actions/requestFormActions';
import { BOOKING_TYPE_DIRECT } from '../../consts/variables';
import { C1AU_DOMAIN, CRUISE_AWAY_DOMAIN } from '../../context/markets';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { CruiseInquiryPhoneCard } from '../../pages/Cruise/components/CruiseInquiryPhoneCard';
import { CruisePageSectionHeader } from '../../pages/Cruise/components/SectionHeader/SectionHeader';
import { RequestFormLoader } from './RequestFormLoader';
import { featureFlagEnabled } from '../../helpers/featureFlagEnabled';

interface IRequestForm {
    startHarbour: string;
    pageData: IPageDataReducer;
    cruisePage: ICruiseReducer;
    requestForm: IRequestFormReducer;
    isBookingFlowActive?: boolean;
    belowThresholdPriceMarkets?: boolean;
    onToogle?: (event: any) => void;
    cabin: ICabinInfo;
}

export const RequestForm = (props: IRequestForm) => {
    const {
        pageData,
        cruisePage,
        requestForm,
        startHarbour,
        isBookingFlowActive,
        belowThresholdPriceMarkets,
        cabin,
    } = props;
    const { exchangeRate } = pageData.header;

    const isAustralianMarket =
        pageData.appConfig.portalId === CRUISE_AWAY_DOMAIN ||
        pageData.appConfig.portalId === C1AU_DOMAIN;

    const {
        setAdultsNum,
        setChildrenNum,
        setSelectedSailIndex,
        setChildAge,
        updateRequestFormFields,
        submitRequestForm,
        setAirportCode,
        setChildrenAges,
    } = useActions();

    const { processedCabins, sails, selectedSailIndex, ratesBySail, operator } = cruisePage;
    const [isAllInclusiveSelected, setIsAllInclusiveSelected] = React.useState(false);
    const [isLowTotalBookingValue, setIsLowTotalBookingValue] = React.useState(false);

    const { dualCurrency, defaultCurrency, countryCode } = pageData.appConfig;
    const currency = dualCurrency || defaultCurrency;

    const showEmailOnlyContactCheckbox = featureFlagEnabled(
        pageData.featureFlags,
        'onlyEmailContactCheckbox',
    );

    const personalInfoControlsEnum = usePersonalDataValidation({
        validateNow: false,
        isAllInclusiveSelected,
    });

    const handleAllInclusiveSwitcher = (isAllInclusiveToggled: boolean) => {
        setIsAllInclusiveSelected(isAllInclusiveToggled);
        setAirportCode(null);
    };

    const selectedRates = cruisePage?.rates?.filter(
        (rate) => rate.sail_nid === cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
    );

    const validatePersonalForm = (requestType?: BookingRequestType) => {
        for (const field in personalInfoControlsEnum) {
            personalInfoControlsEnum[field].control.setDirty(true);
        }

        if (requestType) {
            eventTracker.trackG4Event('product_page', {
                event_type: 'button_click',
                button_name:
                    requestType === BOOKING_TYPE_DIRECT ? 'book_online_desktop' : 'inquiry_desktop',
            });
        }
        submitRequestForm(isAllInclusiveSelected, requestType);
    };
    const selectedCabin: ICabinInfo = !cruisePage.loadingRates ? cabin : null;

    const onBoardCredit = getOnBoardCredit(
        cruisePage?.company?.nid,
        selectedCabin?.perPersonPrice,
        cruisePage?.rates,
        undefined,
        pageData.obc?.blacklist,
    );

    const searchUrlSlug = getRouteSlugs(pageData.brandConfig.slugs).searchresults;

    const directBookingUrl = createDirectBookingUrl(
        pageData.appConfig.directBookingUrl,
        pageData.appConfig.languageCode,
        requestForm,
        {
            outboundAirportCode: cruisePage.airports[requestForm.selectedAirportIndex],
            operatorNid: cruisePage.operator.nid,
        },
    );

    const buttonsConfig = updateDirectBookingButtons(
        {
            adultsNum: requestForm.adultsNum,
            childrenNum: requestForm.childrenNum,
            perPersonPrice: selectedCabin?.perPersonPrice,
            includesFlight: cruisePage.includesFlight,
            sailNid: requestForm.sailNid,
        },
        {
            doesCompanySupportDirectBooking: cruisePage.doesCompanySupportDirectBooking,
            doesCompanySupportFlightsForDirectBooking:
                cruisePage.doesCompanySupportFlightsForDirectBooking,
            directBookingConfig: cruisePage.directBookingConfig,
        },
    );

    const shouldShowButtons =
        selectedCabin?.cabinType?.price?.valid ||
        selectedCabin?.cabinType?.price?.string === __('on request', 'dreamlines');

    const directBookingPriceThreshold = getDirectBookingThreshold(countryCode);

    React.useEffect(() => {
        const rate = selectedRates.find(
            (rate) => rate.cabintype_id === selectedCabin?.cabinType.nid,
        );

        const isAllin =
            (isAllInclusiveSelected && !!rate?.all_in_price?.per_person_price) ||
            rate?.cheapest_price?.is_all_inclusive;

        const isLowTotalBookingValue = isAllin
            ? rate?.all_in_price.cabin_price <= directBookingPriceThreshold
            : rate?.cheapest_price.cabin_price <= directBookingPriceThreshold;

        setIsLowTotalBookingValue(isLowTotalBookingValue);
        setIsAllInclusiveSelected(isAllin);
    }, [selectedCabin]);

    React.useEffect(() => {
        if (
            selectedCabin &&
            (requestForm.cabinNid === null || requestForm.cabinNid !== selectedCabin.cabinType.nid)
        ) {
            updateRequestFormFields({
                cabinNid: selectedCabin?.cabinType?.nid,
                cruiseNid: cruisePage?.nid,
                sailNid: cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
                selectedSailIndex: cruisePage?.selectedSailIndex,
            });
        }
        setAirportCode(null);
    }, [
        selectedCabin,
        cruisePage?.nid,
        cruisePage?.sails,
        cruisePage.selectedSailIndex,
        requestForm.cabinNid,
    ]);

    const dateOfDeparture = cruisePage?.sails[cruisePage.selectedSailIndex]?.departure;

    React.useEffect(() => {
        const newChildAges = requestForm.childAges.map((item, index) => {
            const { age, birthday } = item;
            return {
                age,
                birthday,
                index,
                isValid: isChildAgeValid(
                    dateOfDeparture,
                    birthday,
                    cruisePage.nights,
                    cruisePage.company.bookingServiceCode,
                ),
            };
        });

        setChildrenAges(newChildAges);
    }, [dateOfDeparture]);

    return (
        <div className="requestFormContainer">
            <div className="requestFormContainer__inner-wrapper requestFormContainer__inner-wrapper">
                <section className="requestFormContainer__section">
                    <CruisePageSectionHeader>
                        {__('headlines.datesAndPrices', 'dreamlines')}
                    </CruisePageSectionHeader>
                    <div className="requestFormContainer__box">
                        {cruisePage.loadingRates && <RequestFormLoader />}

                        <RequestFormTravelers
                            setChildrenNum={setChildrenNum}
                            setAdultsNum={setAdultsNum}
                            adultsNum={requestForm.adultsNum}
                            dateOfDeparture={dateOfDeparture}
                            cruiseCompanyCode={cruisePage.company.bookingServiceCode}
                            cruiseNights={cruisePage.nights}
                            childrenNum={requestForm.childrenNum}
                            childAges={requestForm.childAges}
                            minAdultAge={requestForm.selectedPax.adult.minAge}
                            isSubmitted={requestForm.isSubmitted}
                            setChildAge={setChildAge}
                        />

                        <RequestFormDates
                            sails={sails}
                            selectedSailIndex={selectedSailIndex}
                            setSelectedSailIndex={setSelectedSailIndex}
                            defaultCurrency={defaultCurrency}
                            currency={currency}
                            exchangeRate={exchangeRate}
                            soldOutBooking={cruisePage.soldOutBooking}
                            passengersNum={(requestForm.passengers || []).length}
                            ratesBySail={ratesBySail}
                        />

                        <RequestFormCabins
                            selectedCabinId={selectedCabin?.cabinType.nid}
                            cabins={processedCabins}
                            sails={sails}
                            exchangeRate={exchangeRate}
                            selectedRates={selectedRates}
                            onToogle={handleAllInclusiveSwitcher}
                            isAllInclusiveSelected={isAllInclusiveSelected}
                        />

                        <RequestFormCabinType
                            selectedCabinId={selectedCabin?.cabinType.nid}
                            cabinsObj={processedCabins || {}}
                            onChange={updateRequestFormFields}
                            selectedRates={selectedRates}
                            isAllInclusiveSelected={isAllInclusiveSelected}
                            passengersNum={(requestForm.passengers || []).length}
                            tariffsDescription={cruisePage.operator.tariffsDescription}
                        />
                    </div>
                </section>

                <section className="requestFormContainer__section">
                    <CruisePageSectionHeader className="fullMode">
                        <span>{__('RF.summaryHeading', 'dreamlines')}</span>
                    </CruisePageSectionHeader>
                    <div className="requestFormContainer__box">
                        {cruisePage.loadingRates && <RequestFormLoader />}
                        <div className="requestFormContainer__finalStepWrapper">
                            <div className="requestFormContainer__finalStepWrapper__leftSide">
                                <RequestFormSummary
                                    cruise={cruisePage}
                                    requestForm={requestForm}
                                    pageData={pageData}
                                    selectedCabinId={selectedCabin?.cabinType.nid}
                                    selectedSailIndex={selectedSailIndex}
                                    cabin={selectedCabin}
                                    departure={sails[selectedSailIndex]?.departure}
                                    isDualCurrency={dualCurrency !== null}
                                    showDirectBookingLabel={buttonsConfig.shouldShowDirectBooking}
                                    searchUrlSlug={searchUrlSlug}
                                    startHarbour={startHarbour}
                                    isAllInclusive={isAllInclusiveSelected}
                                    selectedRates={selectedRates}
                                />
                                <CruiseInquiryPhoneCard
                                    cabin={selectedCabin}
                                    phoneNumber={pageData.brandConfig.phoneNumber}
                                    officeHours={pageData.header.headerContent.officeHours}
                                />
                                {!operator.isDreamlinesPackage && (
                                    <div
                                        className="requestFormContainer__text"
                                        dangerouslySetInnerHTML={{
                                            __html: __(
                                                'dreamlinesIntermediary',
                                                'dreamlines',
                                            ).replace(
                                                /%cruiseline%/,
                                                cruisePage?.company.title || '',
                                            ),
                                        }}
                                    />
                                )}
                            </div>

                            <div className="requestFormContainer__finalStepWrapper__rightSide">
                                <RequestFormPersonalInfo
                                    updateRequestFormFields={updateRequestFormFields}
                                    requestForm={requestForm}
                                    controlsEnum={personalInfoControlsEnum}
                                    alternativeCruise={cruisePage?.alternativeCruise}
                                    showEmailOnlyContactCheckbox={showEmailOnlyContactCheckbox}
                                    isSubmitError={requestForm.isSubmitError}
                                    apiValidationErrors={requestForm.apiValidationErrors}
                                />

                                {shouldShowButtons && (
                                    <RequestFormButtons
                                        showLastCheckMark={
                                            !cruisePage?.company?.title.includes('TUI')
                                        }
                                        buttonsConfig={buttonsConfig}
                                        directBookingUrl={directBookingUrl}
                                        onBoardCredit={onBoardCredit}
                                        onRequest={
                                            selectedCabin?.cabinType?.price?.valid ||
                                            selectedCabin?.cabinType?.price?.string ===
                                                __('on request', 'dreamlines')
                                        }
                                        validatePersonalForm={() => validatePersonalForm()}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    {isAustralianMarket && (
                        <div className="requestFormContainer__disclaimer-text">
                            {__('priceDisclaimer', 'dreamlines')}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};
